'use client';

import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { JobCardFormData } from './JobCardWizard';
import { Car } from 'lucide-react';
import { getSession } from '@/lib/technicianSession';

const BRANCHES = [
  'Bloemfontein',
  'Aliwal North',
  'Bethlehem',
  'Welkom',
  'Klerksdorp',
  'Vryburg',
  'Kuruman',
  'Upington',
  'Springbok',
  'De Aar',
  'Kimberly',
];

const makes = ['Toyota', 'Volkswagen', 'Ford', 'Isuzu', 'Hyundai', 'BMW', 'Mercedes-Benz', 'Nissan'];

export default function Step1VehicleDetails() {
  const { register, formState: { errors }, setValue } = useFormContext<JobCardFormData>();

  // Pre-fill technician and branch from clocked-in session
  useEffect(() => {
    const session = getSession();
    if (session) {
      setValue('technician', session.name);
      setValue('branch', session.branch);
    }
  }, [setValue]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
          <Car size={18} className="text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Vehicle & Assignment Details</h2>
          <p className="text-xs text-muted-foreground">Enter the vehicle registration, VIN, and technician assignment</p>
        </div>
      </div>

      {/* Row 1: Job meta */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="label-field">Job Number</label>
          <input
            {...register('jobNumber')}
            className="input-field font-mono-data"
            readOnly
          />
          <p className="field-hint">Auto-assigned — read only</p>
        </div>
        <div>
          <label className="label-field">Date</label>
          <input
            {...register('date', { required: 'Date is required' })}
            className="input-field"
            placeholder="DD/MM/YYYY"
          />
          {errors.date && <p className="field-error">{errors.date.message}</p>}
        </div>
        <div>
          <label className="label-field">Branch</label>
          <select
            {...register('branch', { required: 'Branch is required' })}
            className="input-field"
          >
            <option value="">Select branch…</option>
            {BRANCHES.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          {errors.branch && <p className="field-error">{errors.branch.message}</p>}
          <p className="field-hint">Pre-filled from your session</p>
        </div>
      </div>

      {/* Row 2: Technician */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label-field">Assigned Technician</label>
          <input
            {...register('technician', { required: 'Technician is required' })}
            className="input-field bg-secondary/30"
            placeholder="Enter technician name"
            readOnly
          />
          <p className="field-hint">Locked to your clocked-in session</p>
          {errors.technician && <p className="field-error">{errors.technician.message}</p>}
        </div>
        <div>
          <label className="label-field">Mileage (km)</label>
          <input
            {...register('mileage', { required: 'Mileage is required' })}
            type="number"
            className="input-field font-mono-data"
            placeholder="e.g. 87450"
          />
          {errors.mileage && <p className="field-error">{errors.mileage.message}</p>}
        </div>
      </div>

      {/* Row 3: Vehicle identifiers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label-field">Vehicle Registration</label>
          <input
            {...register('vehicleReg', {
              required: 'Vehicle registration is required',
              pattern: { value: /^[A-Z]{2,3}\s\d{2,3}-\d{3}$/i, message: 'Format: GP 23-441' },
            })}
            className="input-field font-mono-data uppercase"
            placeholder="e.g. GP 23-441"
          />
          {errors.vehicleReg && <p className="field-error">{errors.vehicleReg.message}</p>}
          <p className="field-hint">South African registration format</p>
        </div>
        <div>
          <label className="label-field">VIN (Vehicle Identification Number)</label>
          <input
            {...register('vin', {
              required: 'VIN is required',
              minLength: { value: 17, message: 'VIN must be exactly 17 characters' },
              maxLength: { value: 17, message: 'VIN must be exactly 17 characters' },
            })}
            className="input-field font-mono-data uppercase"
            placeholder="e.g. WVWZZZ1KZ8W123441"
            maxLength={17}
          />
          {errors.vin && <p className="field-error">{errors.vin.message}</p>}
          <p className="field-hint">17-character alphanumeric identifier</p>
        </div>
      </div>

      {/* Row 4: Make / Model / Year */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="label-field">Make</label>
          <select {...register('make', { required: 'Make is required' })} className="input-field">
            <option value="">Select make…</option>
            {makes.map((m) => <option key={`make-${m}`} value={m}>{m}</option>)}
          </select>
          {errors.make && <p className="field-error">{errors.make.message}</p>}
        </div>
        <div>
          <label className="label-field">Model</label>
          <input
            {...register('model', { required: 'Model is required' })}
            className="input-field"
            placeholder="e.g. Hilux 2.8 GD-6"
          />
          {errors.model && <p className="field-error">{errors.model.message}</p>}
        </div>
        <div>
          <label className="label-field">Year</label>
          <input
            {...register('year', {
              required: 'Year is required',
              pattern: { value: /^\d{4}$/, message: 'Enter a valid 4-digit year' },
            })}
            type="number"
            className="input-field font-mono-data"
            placeholder="e.g. 2021"
            min={1990}
            max={2026}
          />
          {errors.year && <p className="field-error">{errors.year.message}</p>}
        </div>
      </div>
    </div>
  );
}